import { FC, Suspense, memo } from 'react'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorComponentProps, RouterProvider } from '@tanstack/react-router'
import { BPRouterItems } from 'commons/types/enums'
import { TanStackQueryDevToolsWrapper } from 'commons/utils/TanStackQueryDevToolsWrapper'
import { Logger } from 'commons/utils/logger'
import { NotFound } from 'modules/404'
import { CircularProgress } from 'ui/components'
import { TechnicalIssues } from 'ui/temp'
import { useAuth } from '~auth/AuthContext'

import { BPRouter } from './BPRouter'
import { getBPAccessibleRoutingFlags } from './getBPAccessibleRoutingFlags'

export interface RoutesProps {
  accessibleRoutes?: BPRouterItems[]
  showConnectionsSectionMenu?: boolean
}

export const AppRoutes: FC<RoutesProps> = memo((props) => {
  const { accessibleRoutes, showConnectionsSectionMenu } = props

  const auth = useAuth()

  if (!accessibleRoutes && auth.hasSession) {
    return <CircularProgress centered />
  }

  const queryClientV5 = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 0
      }
    }
  })

  return (
    <Suspense fallback={<CircularProgress centered />}>
      <QueryClientProvider client={queryClientV5}>
        <TanStackQueryDevToolsWrapper />
        <RouterProvider
          router={BPRouter}
          context={{
            accessibleRoutingFlags: getBPAccessibleRoutingFlags(accessibleRoutes ?? []),
            showConnectionsSectionMenu,
            queryClient: queryClientV5,
            auth: auth
          }}
          defaultErrorComponent={(props: ErrorComponentProps) => {
            Logger.getInstance().error(props.error.message, props.error)
            return <TechnicalIssues error={(props.error as any).httpCode} />
          }}
          defaultNotFoundComponent={() => <NotFound />}
          defaultPendingComponent={() => <CircularProgress centered />}
        />
      </QueryClientProvider>
    </Suspense>
  )
})
